import React, { useMemo, useEffect, useCallback, useRef } from 'react'
import CookieConsent from 'react-cookie-consent'
import HeaderItems from './HeaderItems'
import Header from './components/Header'
import Section from './components/Section'
import Footer from './components/Footer'
import CustomChartWarnModal from './components/CustomChartWarnModal'
import StepNav from './components/Workflow/StepNav'
import baseCharts from './charts'
import useProjectSession from './hooks/useProjectSession'
import { BRAND_CONFIG, applyDocumentBranding } from './branding/brandConfig'
import { onStepChanged } from './gaEvents'
import { clearUrlState } from './services/urlState'
import LoadStep from './components/WorkflowSteps/LoadStep'
import IntroStep from './components/WorkflowSteps/IntroStep'
import MapStep from './components/WorkflowSteps/MapStep'
import StyleStep from './components/WorkflowSteps/StyleStep'
import ExportStep from './components/WorkflowSteps/ExportStep'
import { DataProvider, useDataContext } from './contexts/DataContext'
import { ChartProvider, useChartContext } from './contexts/ChartContext'
import { UIProvider, useUIContext } from './contexts/UIContext'
import CanvasLab from './labs/CanvasLab'
import { clearCanvasReturnProject, readCanvasReturnProject } from './labs/canvasWorkspace'

// #TODO: i18n

function AppShell() {
  const canvasReturnStartedRef = useRef(false)
  const { data, userData, userInput, userDataType, dataSource, loading } =
    useDataContext()

  const {
    currentChart,
    mapping,
    visualOptions,
    brandTheme,
    setBrandTheme,
    chartColorSyncMode,
    setChartColorSyncMode,
    lastAppliedBrandPaletteSignature,
    setLastAppliedBrandPaletteSignature,
    toConfirmCustomChart,
    confirmCustomChartLoad,
    abortCustomChartLoad,
    importProject,
    exportProject,
  } = useChartContext()

  const {
    currentStep,
    setCurrentStep,
    showIntro,
    setShowIntro,
    activeMapTab,
    setActiveMapTab,
    activeStyleTab,
    setActiveStyleTab,
    previewSizePreset,
    setPreviewSizePreset,
    previewCustomSize,
    setPreviewCustomSize,
    workflowSteps,
    canAdvance,
    goToNextStep,
    goToPreviousStep,
    mappingComplete,
    handleStartBlank,
    handleTemplateProjectLoad,
  } = useUIContext()

  const canPersistSession = useMemo(
    () => Boolean(data?.dataTypes && userData && currentChart),
    [currentChart, data, userData]
  )

  const sessionSnapshot = useMemo(
    () => ({
      userInput,
      userDataType,
      dataSource,
      currentChartId: currentChart?.metadata?.id || null,
      mapping,
      visualOptions,
      brandTheme,
    }),
    [userInput, userDataType, dataSource, currentChart, mapping, visualOptions, brandTheme]
  )

  const { sessionStatus, sessionBootstrapComplete, clearSavedSession, sessionStatusLabel } = useProjectSession({
    baseCharts,
    importProject,
    exportProject,
    canPersistSession,
    sessionSnapshot,
    brandTheme,
    currentStep,
    activeMapTab,
    activeStyleTab,
    previewSizePreset,
    previewCustomSize,
    chartColorSyncMode,
    lastAppliedBrandPaletteSignature,
    setBrandTheme,
    setCurrentStep,
    setActiveMapTab,
    setActiveStyleTab,
    setPreviewSizePreset,
    setPreviewCustomSize,
    setChartColorSyncMode,
    setLastAppliedBrandPaletteSignature,
  })

  useEffect(() => {
    applyDocumentBranding()
  }, [])

  useEffect(() => {
    if (sessionStatus === 'restored') {
      setShowIntro(false)
    }
  }, [sessionStatus, setShowIntro])

  // A Canvas chart returns through the same importer used by .lugsail files.
  // Wait for the normal autosave restore first: otherwise its asynchronous
  // import can race this one and wipe the Canvas chart's mapping.
  useEffect(() => {
    if (canvasReturnStartedRef.current) return
    if (!sessionBootstrapComplete) return
    const handoff = readCanvasReturnProject()
    if (!handoff) return
    const chart = baseCharts.find((candidate) => candidate.metadata.id === handoff.canvasChartId)
    if (!chart) return
    canvasReturnStartedRef.current = true
    const { canvasChartId, brandTheme: canvasBrandTheme, ...project } = handoff
    importProject({ ...project, currentChart: chart })
      .then(() => {
        clearCanvasReturnProject()
        if (canvasBrandTheme) {
          setBrandTheme((currentTheme) => ({ ...currentTheme, ...canvasBrandTheme }))
        }
        setShowIntro(false)
        setCurrentStep('style')
      })
      .catch((error) => {
        canvasReturnStartedRef.current = false
        console.error('Could not open Canvas chart in Lugsail.', error)
      })
  }, [importProject, sessionBootstrapComplete, setBrandTheme, setCurrentStep, setShowIntro])

  const renderStepActions = () => (
    <div className="workflow-step-actions">
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={goToPreviousStep}
        disabled={currentStep === 'load'}
      >
        Back
      </button>
      {currentStep !== 'export' && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={goToNextStep}
          disabled={!canAdvance[currentStep]}
        >
          Next
        </button>
      )}
    </div>
  )

  return (
    <div className="App">
      <Header
        menuItems={HeaderItems}
        sessionStatusLabel={sessionStatusLabel}
        onClearSavedSession={clearSavedSession}
        onLogoClick={() => { clearUrlState(); setShowIntro(true) }}
        currentStep={showIntro ? null : currentStep}
      />
      <CustomChartWarnModal
        toConfirmCustomChart={toConfirmCustomChart}
        confirmCustomChartLoad={confirmCustomChartLoad}
        abortCustomChartLoad={abortCustomChartLoad}
      />
      <div className="app-sections">
        {showIntro ? (
          <IntroStep
            onTemplateLoaded={handleTemplateProjectLoad}
            onStartBlank={handleStartBlank}
          />
        ) : (
          <>
            <div className="workflow-progress-strip">
              <div className="workflow-progress-strip-inner">
                <span className="workflow-progress-label">
                  Build your chart
                  {loading && <span className="workflow-topbar-spinner" />}
                </span>
                <StepNav
                  steps={workflowSteps}
                  currentStep={currentStep}
                  onStepChange={(nextStep) => {
                    onStepChanged(nextStep, currentStep)
                    setCurrentStep(nextStep)
                  }}
                />
              </div>
            </div>
            <Section>
              <div className="workflow-workspace-shell">
                {currentStep === 'load' && (
                  <LoadStep actions={renderStepActions()} />
                )}
                {currentStep === 'map' && data && currentChart && (
                  <MapStep actions={renderStepActions()} />
                )}
                {currentStep === 'style' && data && currentChart && (
                  <StyleStep actions={renderStepActions()} />
                )}
                {currentStep === 'export' && data && currentChart && mappingComplete && (
                  <ExportStep actions={renderStepActions()} />
                )}
              </div>
            </Section>
          </>
        )}
        <Footer />
        <CookieConsent
          location="bottom"
          buttonText="Got it!"
          style={{ background: 'var(--gray-bg)', color: 'var(--gray-700)' }}
          buttonStyle={{
            background: 'var(--gray-700)',
            color: 'white',
            fontSize: '13px',
            borderRadius: 'var(--radius-sm)',
            padding: '5px 20px',
          }}
          buttonClasses="btn btn-default btn-grey"
          acceptOnScroll={true}
        >
          This website uses Google Analytics to anonymously collect browsing
          data.{' '}
          <a
            href={BRAND_CONFIG.privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 text-body border-bottom border-dark"
          >
            Project source
          </a>
        </CookieConsent>
      </div>
      {/* <ScreenSizeAlert /> */}
    </div>
  )
}

export default function App() {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/'

  if (normalizedPath === '/labs/canvas') {
    return <CanvasLab />
  }

  return (
    <DataProvider>
      <ChartProvider>
        <UIProvider>
          <AppShell />
        </UIProvider>
      </ChartProvider>
    </DataProvider>
  )
}
